import React, { useCallback, useEffect, useState } from 'react';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, Input, InputLabel, NativeSelect } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useUrlUpdater } from '../../hooks/use-url-updater';
import CornRXAdjust from './CornRXAdjust';
import CornRXRun from './CornRXRun';
import DraggableModal from '../../components/DraggableModal';
import { downloadCornRxPdfs, downloadCornRxFields } from '../../lib/api';
import { generateCsvDownload } from '../../lib/csv';
import { getRounded } from '../../lib/numbers';
import { Table } from '../../components/table/Table';
import { DownloadButton } from '../../components/DownloadButton';
import { selectors as fieldSelectors } from '../../store/dux/fields-dux';
import { selectors as authSelectors } from '../../store/dux/auth-dux';
import { actions, selectors } from '../../store/dux/cornrx-dux';
import { usePermissions } from '../../hooks/use-permissions';
import { useMapRefresh } from '../../components/google/maps/use-map-refresh';
import { useFields } from '../../hooks/use-fields';
import { useGrowers } from '../../hooks/use-growers';
import { SendToDeereButton } from '../../components/deere/SendToDeereButton';
import { TableStatus } from '../../components/table/TableStatus';

const planterSpacing = [
  { key: '20', value: '20' },
  { key: '30', value: '30' },
];

const gridRowValueOrNA = ({ value }) => value || '';

export default function CornRXGrid() {
  const dispatch = useDispatch();
  const { updateParams } = useUrlUpdater();
  const [open, setOpen] = useState(false);
  const [runRx, setRunRx] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const { selectedId: growerId } = useGrowers();
  const fields = useSelector(fieldSelectors.growerFields);
  const selectedSpacing = useSelector(selectors.planterSpacing);
  const cornRxFields = useSelector(selectors.cornrxFields)(fields);
  const isLoading = useSelector(selectors.isLoading);
  const { selectedAaids } = useFields();
  const { refresh } = useMapRefresh();
  const isAdministrator =
    useSelector(authSelectors.currentUser)?.userType === 'administrator';
  const permissions = usePermissions();

  const loadCornRX = useCallback(loadCornRxCallback, [
    setIsLoaded,
    dispatch,
    growerId,
  ]);
  const isAdjustDisabled = useCallback(isAdjustDisabledCallback, [isLoaded]);

  useEffect(loadCornRX, [loadCornRX]);

  useEffect(() => {
    updateParams({ spacing: selectedSpacing });
  }, [selectedSpacing, updateParams]);

  function loadCornRxCallback() {
    dispatch(actions.loadGrowerHasCornRX(growerId));
    setIsLoaded(true);
  }
  function isAdjustDisabledCallback(row) {
    return !isLoaded || !row.Avg;
  }

  const loadCornRXData = (selectedAAID) => {
    dispatch(actions.loadCornRXData(selectedAAID));
  };

  const handleAdjClick = (field) => () => {
    loadCornRXData(field.aaManagedId);
    setSelectedRow(field);
    setOpen(true);
  };

  const handleCreateRXClick = () => {
    setRunRx(true);
  };

  const refreshData = async () => {
    await dispatch(actions.loadGrowerHasCornRX(growerId));
    refresh();
  };

  const downloadShapefiles = () =>
    downloadCornRxFields(growerId, getDownloadableAaids(), selectedSpacing);

  function downloadPdf() {
    return downloadCornRxPdfs(
      growerId,
      getDownloadableAaids(),
      selectedSpacing,
    );
  }

  const setSelectedSpacing = (spacing) => {
    dispatch(actions.setPlanterSpacing(spacing));
  };

  const handlePlanterSpacingChange = (event) => {
    setSelectedSpacing(event.target.value.trim());
  };

  const downloadCsv = () => {
    const filename = `cornrx-export-${growerId}.csv`;

    const csvArr = !selectedAaids.length
      ? cornRxFields
      : cornRxFields.filter((f) =>
          selectedAaids.includes(f.aaManagedId.toUpperCase()),
        );

    const csvData = csvArr.map((f) => ({
      field_name: f.fieldName,
      farm_name: f.farmName,
      field_id: f.fieldId,
      acres: getRounded(f.acres, 2),
      min_rate: f.Min,
      max_rate: f.Max,
      avg_rate: f.Avg,
      run_date: f.started ? f.started : 'N/A',
      soil_date: f.soildate ? f.soildate : 'N/A',
      total_seeds: f.TotalSeeds,
      units_per_field: f.UnitsPerField,
      rx_status: f.status ? f.status : 'N/A',
      rx_type: f.rx ? f.rx : 'N/A',
      aaid: f.aaManagedId,
    }));

    generateCsvDownload(csvData, filename);
  };

  function isFieldDownloadable(aaid) {
    return cornRxFields.find((f) => f.aaManagedId === aaid)?.UnitsPerField;
  }

  function getDownloadableAaids() {
    return selectedAaids.filter(isFieldDownloadable);
  }

  function getDownloadablePrescriptions() {
    const aaids = getDownloadableAaids();
    return cornRxFields?.filter((f) => aaids.includes(f.aaManagedId));
  }

  function buildColumns() {
    return [
      {
        field: 'plannedCrop',
        headerName: 'Planned Crop',
        flex: 1,
      },
      {
        field: 'Min',
        type: 'number',
        headerName: 'Min Rate',
        flex: 1,
        valueFormatter: ({ value }) => gridRowValueOrNA({ value }),
      },
      {
        field: 'Max',
        type: 'number',
        headerName: 'Max Rate',
        flex: 1,
        valueFormatter: ({ value }) => gridRowValueOrNA({ value }),
      },
      {
        field: 'Avg',
        type: 'number',
        headerName: 'AVG Rate',
        flex: 1,
        valueFormatter: ({ value }) => gridRowValueOrNA({ value }),
      },
      {
        field: 'started',
        type: 'date',
        headerName: 'Created Date',
        flex: 1,
        valueFormatter: ({ value }) =>
          value ? new Date(value).toLocaleDateString() : null,
      },
      {
        field: 'soildate',
        type: 'date',
        headerName: 'Soil Date',
        flex: 1,
        valueFormatter: ({ value }) =>
          value ? new Date(value).toLocaleDateString() : null,
      },
      {
        field: 'TotalSeeds',
        type: 'number',
        headerName: 'Total Seeds',
        flex: 1,
        valueFormatter: ({ value }) =>
          gridRowValueOrNA({ value }).toLocaleString(),
      },
      {
        field: 'UnitsPerField',
        type: 'number',
        headerName: 'Total Units',
        flex: 1,
        valueFormatter: ({ value }) =>
          gridRowValueOrNA({ value: parseInt(value, 10) }),
      },
      {
        field: 'status',
        type: 'string',
        headerName: 'Rx Status',
        flex: 1,
        renderCell: ({ row }) =>
          isLoading ? null : (
            <TableStatus
              status={[row.status || 'N/A', row.statusdetails]
                .filter(Boolean)
                .join(': ')}
            />
          ),
      },
      {
        field: 'rx',
        type: 'string',
        headerName: 'RX Type',
        flex: 1,
        valueFormatter: gridRowValueOrNA,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Adjust',
        getActions: ({ row }) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Adjust"
            onClick={handleAdjClick(row)}
            disabled={isAdjustDisabled(row)}
            data-testid={`adjust-${row.aaManagedId}`}
          />,
        ],
      },
    ];
  }

  return (
    <Box sx={{ height: '100%' }}>
      <Table
        loading={isLoading}
        columns={buildColumns()}
        fields={cornRxFields}
        preActions={buildSpacingSelector()}
        actions={buildActions()}
        isLpkContractRestricted={true}
      />

      {buildAdjust()}
      {buildCoefficients()}
    </Box>
  );

  function buildAdjust() {
    return open ? (
      <CornRXAdjust
        formopen={() => setOpen(false)}
        selectedField={selectedRow}
        refreshData={refreshData}
      />
    ) : null;
  }

  function buildCoefficients() {
    return runRx ? (
      <DraggableModal
        handleClose={() => setRunRx(false)}
        title="Generate New RX"
        noMinHeight={true}
      >
        <CornRXRun formopen={() => setRunRx(false)} />
      </DraggableModal>
    ) : null;
  }

  function buildSpacingSelector() {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <InputLabel htmlFor="row-spacing">Planter Row Spacing</InputLabel>
        <Box>
          <NativeSelect
            style={{ color: '#000', marginLeft: '10px', width: '55px' }}
            value={selectedSpacing}
            onChange={handlePlanterSpacingChange}
            input={<Input name="rowSpacing" id="row-spacing" />}
            variant="filled"
            data-testid="planter-spacing-selector"
          >
            <option value="" />
            {planterSpacing.map((plntSpace) => (
              <option
                value={plntSpace.value}
                key={plntSpace.key}
                data-testid={`planter-spacing-${plntSpace.value}`}
              >
                {plntSpace.value}
              </option>
            ))}
          </NativeSelect>
        </Box>
      </div>
    );
  }

  function buildActions() {
    return [
      isAdministrator && (
        <Button
          key="rx"
          disabled={selectedAaids.length < 1}
          onClick={handleCreateRXClick}
        >
          Create RX
        </Button>
      ),
      permissions.includes('DOWNLOAD_RX') && (
        <DownloadButton
          key="download"
          keep-open="true"
          disabled={!getDownloadableAaids().length}
          download={downloadShapefiles}
          data-testid="download-btn"
        >
          Download
        </DownloadButton>
      ),
      <DownloadButton key="csv" keep-open="true" download={downloadCsv}>
        Export CSV
      </DownloadButton>,
      <DownloadButton
        key="pdf"
        keep-open="true"
        data-testid="print-pdf-btn"
        disabled={!getDownloadableAaids().length}
        download={downloadPdf}
      >
        Print to PDF
      </DownloadButton>,
      <SendToDeereButton
        type="corn"
        prescriptions={getDownloadablePrescriptions()}
      />,
    ].filter(Boolean);
  }
}
